/**
 * ProductsManager - Gestor de Productos para Facturación
 * CRUD completo para tbl_products
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Inventory,
} from '@mui/icons-material';
import { authenticatedFetch } from '../../services/api';

const UNIDADES_MEDIDA = [
  { code: '59', name: 'Unidad' },
  { code: '60', name: 'Kilogramo' },
  { code: '61', name: 'Gramo' },
  { code: '62', name: 'Metro' },
  { code: '63', name: 'Centímetro' },
  { code: '64', name: 'Litro' },
  { code: '65', name: 'Galón' },
  { code: '99', name: 'Servicio' },
];

const TIPOS_ITEM = [
  { code: 1, name: 'Bien' },
  { code: 2, name: 'Servicio' },
  { code: 3, name: 'Ambos' },
];

const TIPOS_VENTA = [
  { code: 'GRAVADA', name: 'Gravada' },
  { code: 'EXENTA', name: 'Exenta' },
  { code: 'NO_SUJETA', name: 'No Sujeta' },
];

const ProductsManager = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [categories, setCategories] = useState([]);
  
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    price: '',
    cost: '',
    stock: '',
    minStock: '',
    category: '',
    unitMeasure: '59',
    barcode: '',
    dteItemType: 1,
    dteSaleType: 'GRAVADA',
    ivaRate: 13.00,
  });
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Obtener companyId
  const getCompanyId = () => {
    try {
      const modules = localStorage.getItem('nx2026_modules');
      if (modules) {
        const parsed = JSON.parse(modules);
        if (parsed && parsed.length > 0 && parsed[0].company) {
          return parsed[0].company.id;
        }
      }
    } catch (e) {
      console.error('Error getting companyId:', e);
    }
    return null;
  };
  
  // Cargar productos
  const loadProducts = async () => {
    setLoading(true);
    try {
      const companyId = getCompanyId();
      const response = await authenticatedFetch(
        `https://nexai-billing-api.lmendoza-sv.workers.dev/api/products?companyId=${companyId}&limit=100`
      );
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
      
      // Cargar categorías
      const catResponse = await authenticatedFetch(
        `https://nexai-billing-api.lmendoza-sv.workers.dev/api/products/categories?companyId=${companyId}`
      );
      if (catResponse.ok) {
        const catData = await catResponse.json();
        setCategories(catData);
      }
    } catch (error) {
      console.error('Error loading products:', error);
      setError('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadProducts();
  }, []);
  
  // Filtrar productos
  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.code.toLowerCase().includes(search.toLowerCase()) ||
    (p.barcode && p.barcode.includes(search))
  );
  
  // Abrir dialog para nuevo producto
  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        code: product.code || '',
        name: product.name || '',
        description: product.description || '',
        price: product.price || '',
        cost: product.cost || '',
        stock: product.stock || '',
        minStock: product.min_stock || '',
        category: product.category || '',
        unitMeasure: product.unit_measure || '59',
        barcode: product.barcode || '',
        dteItemType: product.dte_item_type || 1,
        dteSaleType: product.dte_sale_type || 'GRAVADA',
        ivaRate: product.iva_rate || 13.00,
      });
    } else {
      setEditingProduct(null);
      setFormData({
        code: '',
        name: '',
        description: '',
        price: '',
        cost: '',
        stock: '',
        minStock: '',
        category: '',
        unitMeasure: '59',
        barcode: '',
        dteItemType: 1,
        dteSaleType: 'GRAVADA',
        ivaRate: 13.00,
      });
    }
    setDialogOpen(true);
  };
  
  // Cerrar dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProduct(null);
    setError('');
  };
  
  // Guardar producto
  const handleSave = async () => {
    setError('');
    
    // Validar - nombre y precio son requeridos, código es opcional
    if (!formData.name || !formData.price) {
      setError('Nombre y precio son requeridos');
      return;
    }
    
    try {
      const companyId = getCompanyId();
      const url = editingProduct
        ? `https://nexai-billing-api.lmendoza-sv.workers.dev/api/products/${editingProduct.id}`
        : 'https://nexai-billing-api.lmendoza-sv.workers.dev/api/products';
      
      const method = editingProduct ? 'PUT' : 'POST';
      
      const body = {
        ...formData,
        companyId,
        // Si code está vacío, el backend generará uno automáticamente
        code: formData.code || null,
        price: parseFloat(formData.price),
        cost: formData.cost ? parseFloat(formData.cost) : 0,
        stock: formData.stock ? parseInt(formData.stock) : 0,
        minStock: formData.minStock ? parseInt(formData.minStock) : 0,
        dteItemType: parseInt(formData.dteItemType),
        ivaRate: parseFloat(formData.ivaRate),
      };
      
      const response = await authenticatedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      
      if (response.ok) {
        setSuccess(editingProduct ? 'Producto actualizado' : 'Producto creado');
        loadProducts();
        handleCloseDialog();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Error al guardar producto');
      }
    } catch (error) {
      console.error('Error saving product:', error);
      setError('Error al guardar producto');
    }
  };
  
  // Confirmar eliminación
  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };
  
  // Eliminar producto
  const handleDelete = async () => {
    try {
      const response = await authenticatedFetch(
        `https://nexai-billing-api.lmendoza-sv.workers.dev/api/products/${productToDelete.id}`,
        { method: 'DELETE' }
      );
      
      if (response.ok) {
        setSuccess('Producto eliminado');
        loadProducts();
        setDeleteDialogOpen(false);
        setProductToDelete(null);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Error al eliminar producto');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      setError('Error al eliminar producto');
    }
  };
  
  return (
    <Box sx={{ width: '100%', p: isMobile ? 1 : 3 }}>
      <Typography variant="h5" gutterBottom>
        <Inventory sx={{ mr: 1, verticalAlign: 'middle' }} />
        Gestión de Productos
      </Typography>
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {/* Buscar y agregar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Buscar productos..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nuevo Producto
        </Button>
      </Box>
      
      {/* Tabla de productos */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Código</strong></TableCell>
              <TableCell><strong>Nombre</strong></TableCell>
              <TableCell><strong>Categoría</strong></TableCell>
              <TableCell align="right"><strong>Precio</strong></TableCell>
              <TableCell align="right"><strong>Stock</strong></TableCell>
              <TableCell><strong>Tipo DTE</strong></TableCell>
              <TableCell align="center"><strong>Acciones</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">Cargando...</TableCell>
              </TableRow>
            ) : filteredProducts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No hay productos. Agregue uno nuevo.
                </TableCell>
              </TableRow>
            ) : (
              filteredProducts.map((product) => (
                <TableRow key={product.id} hover>
                  <TableCell>{product.code}</TableCell>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>
                    <Chip 
                      label={product.category || 'Sin categoría'} 
                      size="small" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">${parseFloat(product.price).toFixed(2)}</TableCell>
                  <TableCell align="right">{product.stock}</TableCell>
                  <TableCell>
                    <Chip 
                      label={product.dte_sale_type || 'GRAVADA'} 
                      size="small" 
                      color={product.dte_sale_type === 'GRAVADA' ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton 
                      color="primary" 
                      onClick={() => handleOpenDialog(product)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      color="error" 
                      onClick={() => handleDeleteClick(product)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Dialog para crear/editar producto */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Código"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                placeholder={editingProduct ? "Código existente" : "Se generará automáticamente si se deja vacío"}
                helperText={!editingProduct && !formData.code ? "Se generará automáticamente (correlativo)" : ""}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Código de Barras"
                value={formData.barcode}
                onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre del Producto"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Precio (con IVA)"
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Costo"
                type="number"
                value={formData.cost}
                onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Unidad de Medida</InputLabel>
                <Select
                  value={formData.unitMeasure}
                  label="Unidad de Medida"
                  onChange={(e) => setFormData({ ...formData, unitMeasure: e.target.value })}
                >
                  {UNIDADES_MEDIDA.map((u) => (
                    <MenuItem key={u.code} value={u.code}>{u.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Stock"
                type="number"
                value={formData.stock}
                onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Stock Mínimo"
                type="number"
                value={formData.minStock}
                onChange={(e) => setFormData({ ...formData, minStock: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                freeSolo
                options={categories}
                value={formData.category || ''}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, category: newValue || '' });
                }}
                onInputChange={(event, newInputValue) => {
                  setFormData({ ...formData, category: newInputValue });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Categoría"
                    helperText="Escriba para buscar o agregar una nueva"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Item DTE</InputLabel>
                <Select
                  value={formData.dteItemType}
                  label="Tipo de Item DTE"
                  onChange={(e) => setFormData({ ...formData, dteItemType: e.target.value })}
                >
                  {TIPOS_ITEM.map((t) => (
                    <MenuItem key={t.code} value={t.code}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Venta DTE</InputLabel>
                <Select
                  value={formData.dteSaleType}
                  label="Tipo de Venta DTE"
                  onChange={(e) => setFormData({ ...formData, dteSaleType: e.target.value })}
                >
                  {TIPOS_VENTA.map((t) => (
                    <MenuItem key={t.code} value={t.code}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Tasa IVA (%)"
                type="number"
                value={formData.ivaRate}
                onChange={(e) => setFormData({ ...formData, ivaRate: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialog de confirmación de eliminación */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          ¿Está seguro de eliminar el producto "{productToDelete?.name}"?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductsManager;
